import axios from 'axios'
import { defineStore } from 'pinia'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { BASE_URL } from '@/environment/environment'
import { logger } from '@/utils/eventLogger'
import type { IDashboardStoreState } from '@/modules/dashboard/types/IDashboardStoreState'
import type { IWidget } from '@/modules/dashboard/types/IWidget'
import type { IChartsResponse } from '@/modules/dashboard/types/IChartsResponse'
import type { IChartItem } from '@/modules/dashboard/types/IChartItem'

export const useDashboard = defineStore('dashboardStore', {
  state: (): IDashboardStoreState => ({
    nextCursor: null,
    widgetsList: null,
    myWidgets: [],
    totalWidgetsCount: 0,
    totalMyWidgetsCount: 0,
    widgetsTypeTree: [],
  }),
  getters: {},
  actions: {
    /**
     * Загружает список всех доступных типов виджетов с сервера
     *
     * Основные действия:
     * - Выполняет GET-запрос к эндпоинту /api/dashboard/widgets/
     * - Сохраняет полученные данные в виде исходной структуры в widgetsList
     * - Преобразует данные в древовидную структуру для использования в компоненте FilterTreeSelect
     * - Сохраняет дерево виджетов в widgetsTypeTree
     *
     * Структура дерева:
     * - Родительские узлы: категории графиков (vertical_bar_chart, count, etc.)
     * - Дочерние узлы: конкретные виджеты с их id и названиями
     * - Родительские узлы не выбираются (selectable: false)
     * - Дочерние узлы выбираются (selectable: true) и являются листьями (isLeaf: true)
     */
    async fetchWidgetsList(): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const res = await axios.get(`${BASE_URL}/api/dashboard/widgets/`)
        const data: IChartsResponse = res.data

        this.widgetsList = data
        this.widgetsTypeTree = Object.entries(data).map(([chartType, items]) => ({
          key: `category-${chartType}`,
          label: chartType,
          data: {
            name: chartType,
            position: 0,
            selfId: `category-${chartType}`,
          },
          icon: '',
          selectable: false,
          children: items.map((item: IChartItem) => ({
            key: `widget-${chartType}-${item.id}`,
            label: item.name,
            data: {
              name: item.name,
              position: 0,
              selfId: item.id.toString(),
            },
            icon: '',
            selectable: true,
            children: [],
            isLeaf: true,
          })),
        }))
      } catch (error) {
        console.error(`❌ Ошибка загрузки списка виджетов: ${String(error)}`)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить список виджетов') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список моих виджетов с сервера
     *
     * Основные действия:
     * - Отправляет GET-запрос к эндпоинту /api/dashboard/widgets/my/
     * - Получает массив виджетов пользователя
     * - Сохраняет виджеты в this.myWidgets
     * - Обновляет счетчик this.totalMyWidgetsCount
     *
     * @param {string} [cursor] - Курсор для подгрузки следующей страницы (опционально)
     * @throws {Error} При ошибке сетевого запроса или API
     */
    async fetchMyWidgets(cursor?: string): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}

        if (cursor) params.cursor = cursor

        const res = await axios.get(`${BASE_URL}/api/dashboard/widgets/my/`, { params })
        const data: IWidget[] = res.data

        this.nextCursor = null
        this.myWidgets = data
        this.totalMyWidgetsCount = data.length
      } catch (error) {
        console.error(`❌ Ошибка загрузки моих виджетов: ${String(error)}`)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить мои виджеты') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет порядок пользовательских виджетов на сервере
     *
     * Основные действия:
     * - Отправляет POST-запрос на эндпоинт `/reorder/` с массивом объектов { widget_id, order }
     * - Обновляет массив myWidgets в сторе, присваивая новый order соответствующим виджетам
     * - Возвращает массив обновленных виджетов с актуальными order
     */
    async updateWidgetsOrder(widgetsWithOrder: Array<{ id: number; order: number }>) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // Формируем payload в формате API
        const payload = {
          widget_orders: widgetsWithOrder.map(w => ({
            widget_id: w.id,
            order: w.order,
          })),
        }

        // Отправка запроса на сервер
        const res = await axios.patch(`${BASE_URL}/api/dashboard/widgets/my/reorder/`, payload)

        // Сервер возвращает обновленные виджеты
        const updatedWidgets: IWidget[] = res.data

        // Обновляем myWidgets в сторе
        updatedWidgets.forEach(updated => {
          const index = this.myWidgets.findIndex(w => w.id === updated.id)
          if (index !== -1) {
            this.myWidgets[index] = updated
          }
        })

        return updatedWidgets
      } catch (error) {
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Добавляет виджет в список моих виджетов
     *
     * Основные действия:
     * - Отправляет POST-запрос к эндпоинту /api/dashboard/widgets/my/add/
     * - Передает widget_id выбранного виджета
     * - После успешного добавления обновляет список моих виджетов
     *
     * @param {number} widgetId - ID виджета для добавления
     * @throws {Error} При ошибке сетевого запроса или API
     */
    async addMyWidget(widgetId: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.post(`${BASE_URL}/api/dashboard/widgets/my/add/`, {
          widget_id: widgetId,
        })

        feedbackStore.showToast({ type: 'success', message: 'Виджет успешно добавлен' })
      } catch (error) {
        logger.error('addMyWidget_error', {
          store: 'dashboardStore',
          function: 'addMyWidget',
          error: `❌ Ошибка добавления виджета: ${String(error)}`,
          payload: `widgetId - ${widgetId}`,
        })

        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось добавить виджет'),
          time: 7000,
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет виджет из списка моих виджетов
     *
     * Основные действия:
     * - Отправляет DELETE-запрос к эндпоинту /api/dashboard/widgets/my/delete/
     * - Передает widget_id виджета для удаления в параметрах запроса
     * - После успешного удаления обновляет список моих виджетов
     *
     * @param {number} widgetId - ID виджета для удаления
     * @throws {Error} При ошибке сетевого запроса или API
     */
    async deleteMyWidget(widgetId: number): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/dashboard/widgets/my/delete/`, {
          params: { widget_id: widgetId },
        })
        return true
      } catch (error) {
        logger.error('deleteMyWidget_error', {
          store: 'dashboardStore',
          function: 'deleteMyWidget',
          error: `❌ Ошибка удаления виджета: ${String(error)}`,
          payload: `widgetId - ${widgetId}`,
        })
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось удалить виджет') })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})
